import { api } from './api';
import type {
  CancelSubscriptionRequest,
  ChangePlanRequest,
  CreateCustomerSubscriptionRequest,
  CreateSubscriptionRequest,
  Subscription,
} from '../types/subscription';

class SubscriptionService {
  async getByCustomer(customerId: string): Promise<Subscription | null> {
    try
    {
      const response = await api.get<Subscription>(`/customers/${customerId}/subscription`);
      return response.data;
    }
    catch (error: unknown)
    {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 404)
      {
        return null;
      }

      throw error;
    }
  }

  async createForCustomer(
    customerId: string,
    payload: CreateCustomerSubscriptionRequest
  ): Promise<Subscription> {
    const response = await api.post<Subscription>(
      `/customers/${customerId}/subscription`,
      payload
    );
    return response.data;
  }

  async activateForCustomer(customerId: string, subscriptionId: string): Promise<Subscription> {
    const response = await api.post<Subscription>(
      `/customers/${customerId}/subscription/${subscriptionId}/activate`
    );
    return response.data;
  }

  async changePlanForCustomer(
    customerId: string,
    subscriptionId: string,
    payload: ChangePlanRequest
  ): Promise<Subscription> {
    const response = await api.post<Subscription>(
      `/customers/${customerId}/subscription/${subscriptionId}/change-plan`,
      payload
    );
    return response.data;
  }

  async cancelForCustomer(
    customerId: string,
    subscriptionId: string,
    payload?: CancelSubscriptionRequest
  ): Promise<Subscription> {
    const response = await api.post<Subscription>(
      `/customers/${customerId}/subscription/${subscriptionId}/cancel`,
      payload ?? {}
    );
    return response.data;
  }

  async getAll(): Promise<Subscription[]> {
    const response = await api.get<Subscription[]>('/subscriptions');
    return response.data;
  }

  async getById(id: string): Promise<Subscription> {
    const response = await api.get<Subscription>(`/subscriptions/${id}`);
    return response.data;
  }

  async create(payload: CreateSubscriptionRequest): Promise<Subscription> {
    const response = await api.post<Subscription>('/subscriptions', payload);
    return response.data;
  }

  async getByCustomerAndAccount(planId: string, accountId: string): Promise<Subscription[]> {
    const response = await api.get<Subscription[]>('/subscriptions/by-customer-account', {
      params: { planId, accountId },
    });
    return response.data;
  }

  async activate(id: string): Promise<Subscription> {
    const response = await api.post<Subscription>(`/subscriptions/${id}/activate`);
    return response.data;
  }

  async changePlan(id: string, payload: ChangePlanRequest): Promise<Subscription> {
    const response = await api.post<Subscription>(`/subscriptions/${id}/change-plan`, payload);
    return response.data;
  }

  async cancel(id: string, payload?: CancelSubscriptionRequest): Promise<Subscription> {
    const response = await api.post<Subscription>(`/subscriptions/${id}/cancel`, payload ?? {});
    return response.data;
  }
}

export const subscriptionService = new SubscriptionService();
